import axios from 'axios';
import { httpApi } from './http';
import type { ApiResponse, Card, PaginationParams } from '../types/card';
import type { MaxRateCardResponse } from '../types/cardRecommendation';
const api = httpApi;

export class CardService {
  static async getMaxRateCard(): Promise<MaxRateCardResponse> {
    try {
      const response = await api.get<MaxRateCardResponse>('/cards/maxrate');

      if (!response.data) {
        throw new Error('No data received from server');
      }

      if (!response.data.success) {
        throw new Error(response.data.error || 'API request failed');
      }

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if ((error.response?.data as any)?.error) {
          throw new Error((error.response?.data as any).error);
        }
        if (error.response?.status) {
          throw new Error(`Server error: ${error.response.status}`);
        }
        if (error.request) {
          throw new Error('Network error: Unable to connect to server');
        }
      }
      throw new Error('Failed to fetch recommended card');
    }
  }

  static async getCards(params: PaginationParams = {}): Promise<ApiResponse> {
    try {
      // Send pagination parameters in the request body
      const requestBody = {
        page: params.page || 1,
        limit: params.limit || 100,
        ...params
      };
      
      const response = await api.post<ApiResponse>('/cards', requestBody);
      
      // Validate the response structure
      if (!response.data) {
        throw new Error('No data received from server');
      }
      
      if (!response.data.success) {
        throw new Error(response.data.error || 'API request failed');
      }
      
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // Handle axios-specific errors
        if (error.response?.data?.error) {
          console.error('API Error:', error.response.data.error);
          throw new Error(error.response.data.error);
        } else if (error.response?.status) {
          console.error(`HTTP Error ${error.response.status}:`, error.response.statusText);
          throw new Error(`Server error: ${error.response.status}`);
        } else if (error.request) {
          console.error('Network Error:', error.message);
          throw new Error('Network error: Unable to connect to server');
        }
      }
      
      console.error('Error fetching cards:', error);
      throw new Error('Failed to fetch cards');
    }
  }

  static async getCardById(idOrSlug: string): Promise<{ success: boolean; data: Card; error?: string }> {
    try {
      const encoded = encodeURIComponent(idOrSlug);

      // Prefer the id endpoint first (works for numeric ids and generated ids like `card-...`).
      // Fall back to the slug endpoint for backward compatibility.
      let response: any;
      try {
        response = await api.get<{ success: boolean; data: Card; error?: string }>(`/cards/${encoded}`);
      } catch (e: any) {
        response = await api.get<{ success: boolean; data: Card; error?: string }>(`/cards/slug/${encoded}`);
      }
      
      // Validate the response structure
      if (!response.data) {
        throw new Error('No data received from server');
      }
      
      if (!response.data.success) {
        throw new Error(response.data.error || 'API request failed');
      }
      
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // Handle axios-specific errors
        if (error.response?.data?.error) {
          console.error('API Error:', error.response.data.error);
          throw new Error(error.response.data.error);
        } else if (error.response?.status) {
          console.error(`HTTP Error ${error.response.status}:`, error.response.statusText);
          throw new Error(`Server error: ${error.response.status}`);
        } else if (error.request) {
          console.error('Network Error:', error.message);
          throw new Error('Network error: Unable to connect to server');
        }
      }
      
      console.error('Error fetching card:', error);
      throw new Error('Failed to fetch card details');
    }
  }

  static async updateCard(id: string, updateData: Partial<Card>): Promise<{ success: boolean; data: Card; error?: string }> {
    try {
      const response = await api.put<{ success: boolean; data: Card; error?: string }>(`/cards/${id}`, updateData);
      
      // Validate the response structure
      if (!response.data) {
        throw new Error('No data received from server');
      }
      
      if (!response.data.success) {
        throw new Error(response.data.error || 'API request failed');
      }
      
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        // Handle axios-specific errors
        if (error.response?.data?.error) {
          console.error('API Error:', error.response.data.error);
          throw new Error(error.response.data.error);
        } else if (error.response?.status) {
          console.error(`HTTP Error ${error.response.status}:`, error.response.statusText);
          throw new Error(`Server error: ${error.response.status}`);
        } else if (error.request) {
          console.error('Network Error:', error.message);
          throw new Error('Network error: Unable to connect to server');
        }
      }
      
      console.error('Error updating card:', error);
      throw new Error('Failed to update card');
    }
  }

  // Rewards APIs
  static async getRewards(params: { cardId: string; page?: number; limit?: number }): Promise<{ success: boolean; data: any[]; count?: number; total?: number; page?: number; pages?: number; error?: string }> {
    try {
      const body = {
        cardId: params.cardId,
        page: params.page ?? 1,
        limit: params.limit ?? 100,
      };
      const response = await api.post<{ success: boolean; data: any[]; count?: number; total?: number; page?: number; pages?: number; error?: string }>(`/rewards`, body);
      if (!response.data) {
        throw new Error('No data received from server');
      }
      if (!response.data.success) {
        throw new Error(response.data.error || 'API request failed');
      }
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.data?.error) {
          console.error('API Error:', error.response.data.error);
          throw new Error(error.response.data.error);
        } else if (error.response?.status) {
          console.error(`HTTP Error ${error.response.status}:`, error.response.statusText);
          throw new Error(`Server error: ${error.response.status}`);
        } else if (error.request) {
          console.error('Network Error:', error.message);
          throw new Error('Network error: Unable to connect to server');
        }
      }
      console.error('Error fetching rewards:', error);
      throw new Error('Failed to fetch rewards');
    }
  }

  static async getRewardById(rewardId: string): Promise<{ success: boolean; data: any; error?: string }> {
    try {
      const response = await api.get<{ success: boolean; data: any; error?: string }>(`/rewards/${rewardId}`);
      if (!response.data) {
        throw new Error('No data received from server');
      }
      if (!response.data.success) {
        throw new Error(response.data.error || 'API request failed');
      }
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.data?.error) {
          console.error('API Error:', error.response.data.error);
          throw new Error(error.response.data.error);
        } else if (error.response?.status) {
          console.error(`HTTP Error ${error.response.status}:`, error.response.statusText);
          throw new Error(`Server error: ${error.response.status}`);
        } else if (error.request) {
          console.error('Network Error:', error.message);
          throw new Error('Network error: Unable to connect to server');
        }
      }
      console.error('Error fetching reward detail:', error);
      throw new Error('Failed to fetch reward detail');
    }
  }

  static async updateReward(rewardId: string, updateData: any): Promise<{ success: boolean; data: any; error?: string }> {
    try {
      const response = await api.put<{ success: boolean; data: any; error?: string }>(`/rewards/${rewardId}`, updateData);
      if (!response.data) {
        throw new Error('No data received from server');
      }
      if (!response.data.success) {
        throw new Error(response.data.error || 'API request failed');
      }
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.data?.error) {
          console.error('API Error:', error.response.data.error);
          throw new Error(error.response.data.error);
        } else if (error.response?.status) {
          console.error(`HTTP Error ${error.response.status}:`, error.response.statusText);
          throw new Error(`Server error: ${error.response.status}`);
        } else if (error.request) {
          console.error('Network Error:', error.message);
          throw new Error('Network error: Unable to connect to server');
        }
      }
      console.error('Error updating reward:', error);
      throw new Error('Failed to update reward');
    }
  }

  static async createReward(rewardData: any): Promise<{ success: boolean; data: any; error?: string }> {
    try {
      const response = await api.post<{ success: boolean; data: any; error?: string }>(`/rewards/create`, rewardData);
      if (!response.data) {
        throw new Error('No data received from server');
      }
      if (!response.data.success) {
        throw new Error(response.data.error || 'API request failed');
      }
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response?.data?.error) {
          console.error('API Error:', error.response.data.error);
          throw new Error(error.response.data.error);
        } else if (error.response?.status) {
          console.error(`HTTP Error ${error.response.status}:`, error.response.statusText);
          throw new Error(`Server error: ${error.response.status}`);
        } else if (error.request) {
          console.error('Network Error:', error.message);
          throw new Error('Network error: Unable to connect to server');
        }
      }
      console.error('Error creating reward:', error);
      throw new Error('Failed to create reward');
    }
  }
}

export default CardService;
